import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, tap, withLatestFrom } from 'rxjs';
import { AccountClientService } from '@mode/shared/data-access-webapp';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as ScheduleActions from './schedule.actions';
import * as ScheduleSelectors from './schedule.selectors';
import { SchedulesTypes } from '@mode/shared/contract-common';
import { getOwnerUsername } from '@mode/shared/util-js';
import { ToastService } from '@mode/shared/util-toast';
import { ScheduleFetchService } from 'libs/shared/data-access-webapp/src/lib/accounts/schedule-fetch.service';
import { ScheduleIntervalResource } from 'libs/shared/contract-common/src/lib/schedules.types';

@Injectable()
export class ScheduleEffects {
  loadSchedules$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ScheduleActions.loadSchedules),
      withLatestFrom(
        this.store.select(ScheduleSelectors.selectSchedulesFilters),
        this.store.select(ScheduleSelectors.selectSchedulesSort)
      ),
      switchMap(([action, filters, sort]) =>
        this.client.getSchedules(action.username, action.page, action.query, filters, sort).pipe(
          map((schedules: SchedulesTypes.Schedules) =>
            ScheduleActions.loadSchedulesSuccess({ pagination: schedules.pagination, schedules: schedules.schedules })
          ),
          catchError((error) => of(ScheduleActions.loadSchedulesFailure({ error })))
        )
      )
    );
  });

  deleteSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.deleteSchedule),
      switchMap((action) =>
        this.client.deleteSchedule(action.token).pipe(
          map(() => ScheduleActions.deleteScheduleSuccess({ token: action.token })),
          catchError((error: Error) => of(ScheduleActions.deleteScheduleFailure({ error })))
        )
      )
    )
  );

  notifyScheduleDeleted$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScheduleActions.deleteScheduleSuccess),
        tap(() => this.toastService.success('Deleted schedule'))
      );
    },
    { dispatch: false }
  );

  notifyScheduleNotDeleted$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScheduleActions.deleteScheduleFailure),
        tap(() => this.toastService.error('Could not delete schedule'))
      );
    },
    { dispatch: false }
  );

  bulkDeleteSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.bulkDeleteSchedule),
      switchMap((action) =>
        this.client.bulkDeleteSchedule(action.payload).pipe(
          map(() => ScheduleActions.bulkDeleteScheduleSuccess({ payload: action.payload })),
          catchError((error: Error) => of(ScheduleActions.bulkDeleteScheduleFailure({ error })))
        )
      )
    )
  );

  notifyBulkScheduleDeleted$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScheduleActions.bulkDeleteScheduleSuccess),
        tap(() => this.toastService.success('Schedule(s) have successfully been deleted '))
      );
    },
    { dispatch: false }
  );

  notifyBulkScheduleNotDeleted$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScheduleActions.bulkDeleteScheduleFailure),
        tap(() => this.toastService.error('Schedule(s) could not be deleted. Please try again. '))
      );
    },
    { dispatch: false }
  );

  reloadSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ScheduleActions.reloadSchedules,
        ScheduleActions.updateSchedulesFilter,
        ScheduleActions.updateSchedulesSort
      ),
      withLatestFrom(
        this.store.select(ScheduleSelectors.selectSchedulesPagination),
        this.store.select(ScheduleSelectors.selectSchedulesQuery),
        this.store.select(ScheduleSelectors.selectSchedulesFilters),
        this.store.select(ScheduleSelectors.selectSchedulesSort)
      ),
      map(([action, pagination, query, filters, sort]) =>
        ScheduleActions.loadSchedules({
          query,
          page: action.type === ScheduleActions.updateSchedulesFilter.type ? 1 : pagination.page,
          username: getOwnerUsername(window.document) || '',
          filters,
          sort,
        })
      )
    )
  );

  loadScheduleIntervalThreshold$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.loadScheduleIntervalThreshold),
      switchMap((action) =>
        this.scheduleFetchService.getScheduleIntervalThreshold(action.accountID).pipe(
          map((response: SchedulesTypes.ScheduleIntervalResource) =>
            ScheduleActions.loadScheduleIntervalThresholdSuccess({
              minimumScheduleInterval: response.minimum_schedule_interval,
              availableIntervals: response.allowed_schedule_intervals,
            })
          ),
          catchError((error) => of(ScheduleActions.loadScheduleIntervalThresholdFailure({ error })))
        )
      )
    )
  );

  updateScheduleIntervalThreshold$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ScheduleActions.updateScheduleIntervalThreshold),
      switchMap((action) =>
        this.scheduleFetchService.updateScheduleIntervalThreshold(action.accountID, action.interval).pipe(
          map((response: { minimum_schedule_interval: string }) =>
            ScheduleActions.updateScheduleIntervalThresholdSuccess({
              minimumScheduleInterval: response.minimum_schedule_interval,
            })
          ),
          catchError((error) => of(ScheduleActions.updateScheduleIntervalThresholdFailure({ error })))
        )
      )
    );
  });

  notifyScheduleIntervalThresholdUpdated$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScheduleActions.updateScheduleIntervalThresholdSuccess),
        tap(() => this.toastService.success('Schedule interval updated successfully'))
      );
    },
    { dispatch: false }
  );

  notifyScheduleIntervalThresholdUpdateFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScheduleActions.updateScheduleIntervalThresholdFailure),
        tap(() => this.toastService.error('Failed to update schedule interval'))
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private client: AccountClientService,
    private toastService: ToastService,
    private scheduleFetchService: ScheduleFetchService
  ) {}
}
